import type { ChangeEvent, FormEvent } from 'react';
import type { ConsultService, Palette, QuoteFormValues } from './types';

type QuoteProps = {
  palette: Palette;
  consultServices: readonly ConsultService[];
  formError: string | null;
  formSuccess: boolean;
  isSubmittingConsult: boolean;
  selectedServices: string[];
  consultValues: QuoteFormValues;
  handleConsultFieldChange: (field: keyof QuoteFormValues) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleServiceToggle: (key: string) => void;
  handleConsultSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
};

export function QuoteSection({
  palette,
  consultServices,
  formError,
  formSuccess,
  isSubmittingConsult,
  selectedServices,
  consultValues,
  handleConsultFieldChange,
  handleServiceToggle,
  handleConsultSubmit,
}: QuoteProps) {
  const { firstName, lastName, email, phone, address, zip, notes } = consultValues;

  return (
    <section
      id="quote"
      className="px-6 pb-20 scroll-mt-24 md:scroll-mt-28"
      style={{ background: `linear-gradient(120deg, ${palette.forest}, ${palette.sage})` }}
    >
      <div className="mx-auto max-w-5xl rounded-[32px] bg-white/95 p-8 shadow-xl backdrop-blur">
        <div className="text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.4em]" style={{ color: palette.forest }}>
            Tell us your vision. We’ll handle the rest.
          </p>
          <h2 className="mt-3 text-3xl font-semibold text-slate-900">Short quote form. Fast follow-up.</h2>
          <p className="mt-3 text-base text-slate-700">
            Name, phone, ZIP, and which services you want. We reply with a coordinated plan—not a random salesperson call.
          </p>
        </div>
        <form className="mx-auto mt-10 grid gap-4 text-left lg:grid-cols-2" onSubmit={handleConsultSubmit} noValidate>
          {formError ? (
            <p className="lg:col-span-2 rounded-2xl border border-red-400/50 bg-red-50 px-4 py-3 text-sm text-red-700">
              {formError}
            </p>
          ) : null}
          {formSuccess ? (
            <p className="lg:col-span-2 rounded-2xl border border-emerald-300/60 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              Thanks — your quote request is in. We’ll reach out shortly with scheduling options.
            </p>
          ) : null}
          <input
            required
            type="text"
            placeholder="First name"
            className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-500 focus:border-slate-400 focus:outline-none"
            value={firstName}
            onChange={handleConsultFieldChange('firstName')}
          />
          <input
            required
            type="text"
            placeholder="Last name"
            className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-500 focus:border-slate-400 focus:outline-none"
            value={lastName}
            onChange={handleConsultFieldChange('lastName')}
          />
          <input
            required
            type="email"
            placeholder="Email"
            className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-500 focus:border-slate-400 focus:outline-none"
            value={email}
            onChange={handleConsultFieldChange('email')}
          />
          <input
            required
            type="tel"
            placeholder="Phone"
            className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-500 focus:border-slate-400 focus:outline-none"
            value={phone}
            onChange={handleConsultFieldChange('phone')}
          />
          <input
            required
            type="text"
            placeholder="Street address (include city & state)"
            className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-500 focus:border-slate-400 focus:outline-none lg:col-span-2"
            value={address}
            onChange={handleConsultFieldChange('address')}
          />
          <input
            required
            type="text"
            placeholder="ZIP code"
            className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-500 focus:border-slate-400 focus:outline-none"
            value={zip}
            onChange={handleConsultFieldChange('zip')}
          />
          <div className="lg:col-span-1">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-600">Pick up to two services</p>
            <div className="mt-3 grid gap-2">
              {consultServices.map((service) => {
                const active = selectedServices.includes(service.key);
                return (
                  <label
                    key={service.key}
                    className={`flex cursor-pointer items-center justify-between rounded-2xl border px-4 py-3 text-sm font-semibold transition ${
                      active
                        ? 'border-transparent bg-[#D97706]/10 text-slate-900 shadow-sm'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={active}
                      onChange={() => handleServiceToggle(service.key)}
                    />
                    <span>{service.label}</span>
                    <span className="text-xs uppercase tracking-[0.28em] text-slate-500">{active ? 'Selected' : ''}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <textarea
            rows={4}
            placeholder="Short note: roof age, water concerns, preferred timeline."
            className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-500 focus:border-slate-400 focus:outline-none lg:col-span-2"
            value={notes}
            onChange={handleConsultFieldChange('notes')}
          />
          <button
            type="submit"
            disabled={isSubmittingConsult}
            className="rounded-2xl px-6 py-4 text-base font-semibold uppercase tracking-[0.3em] text-white shadow-lg transition hover:-translate-y-0.5 lg:col-span-2 disabled:opacity-60"
            style={{ backgroundColor: palette.clay }}
          >
            {isSubmittingConsult ? 'Sending…' : 'Get my quote'}
          </button>
          <p className="text-xs text-slate-600 lg:col-span-2">
            By submitting you consent to receive updates about your project from Home Pros for Texas.
          </p>
        </form>
      </div>
    </section>
  );
}
